import React from 'react'
import styled from '@emotion/styled'

import {Button} from '@vio/atlas/Button'
import {ButtonLink} from '@vio/atlas/ButtonLink'
import {Divider} from '@vio/atlas/Divider'
import {NoStyleButton} from '@vio/atlas/helpers/NoStyleButton'
import {Icon} from '@vio/atlas/Icon'
import {NavBar, NavBarTitle} from '@vio/atlas/NavBar'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {FilterDisplayContainerProps} from './FilterDisplayContainer'

const Footer = styled.div`
  padding: ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s600};
  display: flex;
  justify-content: flex-end;
  gap: ${cssTheme.layout.spacing.s300};
  z-index: 3;
  background: ${cssTheme.colors.background.neutral.primary};
  position: relative;
`

/** Positioned context so the absolutely-placed close button anchors to the bar. */
const HeaderWrapper = styled.div`
  position: relative;
`

const PopoverCloseButton = styled(NoStyleButton)`
  color: ${cssTheme.colors.content.neutral.secondary};
  height: 24px;
  position: absolute;
  right: ${cssTheme.layout.spacing.s600};
  &:focus-visible {
    outline: 2px solid ${cssTheme.colors.border.neutral.primaryHover};
    outline-offset: 2px;
    border-radius: 4px;
  }
`

type FilterDisplayContentProps = Pick<
  FilterDisplayContainerProps,
  | 'title'
  | 'children'
  | 'onClose'
  | 'onClear'
  | 'onApply'
  | 'dataId'
  | 'filtersCount'
>

export const FilterDisplayContent: React.FC<FilterDisplayContentProps> = ({
  title,
  dataId,
  children,
  filtersCount,
  onClose,
  onClear,
  onApply
}) => {
  return (
    <div>
      <HeaderWrapper>
        <NavBar
          isCompact
          centerAligned
          withDivider
          centerContent={<NavBarTitle>{title}</NavBarTitle>}
          endContent={
            <PopoverCloseButton onClick={onClose}>
              <Icon name="Close" />
            </PopoverCloseButton>
          }
        />
      </HeaderWrapper>

      {children}
      <Divider />
      <Footer data-id={`${dataId}Buttons`}>
        <ButtonLink dataId="clearFiltersButton" onClick={onClear}>
          <FormattedMessageWrapper id="clearAll" defaultMessage="Clear all" />
        </ButtonLink>
        <Button dataId="applyFiltersButton" onClick={onApply}>
          {filtersCount > 0 ? (
            <FormattedMessageWrapper
              id="filters.applyPlural"
              defaultMessage="Apply {count, plural, one {1 filter} other {# filters}}"
              values={{count: filtersCount}}
            />
          ) : (
            <FormattedMessageWrapper id="apply" defaultMessage="Apply" />
          )}
        </Button>
      </Footer>
    </div>
  )
}
